// Per-hex data textures: one texel per hex (COLS × ROWS), read in shaders by texel fetch.
//
//   texture   format          r               g               b                     a
//   ───────   ─────────────   ─────────────   ─────────────   ───────────────────   ──────────────
//   terrain   RGBA8, nearest  TERRAIN         FEATURE         hill + 4·river_size   river edge bits
//   relief    RGBA16F, linear elev (m)        elev_max (m)    ice 0..1 (1)          land 0..1
//   owner     RGBA8, nearest  owner lo byte   owner hi byte   controller lo         controller hi
//   state     RGBA8, nearest  CONTROL         controlFrac     staleness             lens (255 = none)
//   mark      RGBA8, nearest  highlight 0/255 (spare)         (spare)               (spare)
//   ground    RGBA8, nearest  2 texels per hex (x = 2·col, 2·col + 1): heights.ts GROUND weights
//   snow      RGBA16F, near.  snow line y     glacier patch   (spare)               (spare)
//   actors    RGBA8 linear    ACTOR_TEX_W × 3 rows: row 0 primary, row 1 secondary colour, row 2
//                             relation to the viewing leader (RELATION_CODE) per actor index
//
// (1) glacier ice on land (physical grid); on ocean and coast hexes, the denser of the grid's
//     ice (ice shelves) and the snapshot's sea-ice concentration for its month (NSIDC monthly,
//     WorldSnapshot.seaIce, 0 when absent).
//
// Update methods diff against what is already uploaded and flag only the textures that changed.

import * as THREE from 'three/webgpu';
import { float, int, ivec2, textureLoad, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { COLS, HEX_COUNT, ROWS } from '../core/grid.ts';
import { TERRAIN } from '../core/schema.ts';
import type { LensResult, Relation, WorldSnapshot } from '../world/api.ts';
import { GROUND_COUNT, type HexShapes } from './terrain/heights.ts';
import { cellCoord, hexBary, type HexCell, type HexFrag } from './tsl/hex.ts';

type F = Node<'float'>;

/** Physical fields the view needs, by grid field name (physical.grid.bin). */
export interface PhysicalGrid {
  terrain: Uint8Array;
  feature: Uint8Array;
  hill: Uint8Array;
  elev: Int16Array;
  elev_max: Int16Array;
  relief: Uint16Array;
  river: Uint8Array;
  river_size: Uint8Array;
  ice: Uint8Array;
  land: Uint8Array;
  lat: Float32Array;
}

/** Lens texel value meaning "no data" (NaN in LensResult.values). */
export const LENS_NONE = 255;
const LENS_MAX = 254;

/** Actor slots in the colour texture; actor indices at or above this draw as index 0. */
export const ACTOR_TEX_W = 2048;
const ACTOR_ROWS = 3;

/** Relation byte in the actors texture (row 2); 0 = unknown. */
export const RELATION_CODE: Readonly<Record<Relation, number>> = { self: 1, war: 2, ally: 3, friend: 4, neutral: 5, denounced: 6 };

const BYTE = 255;
const RIVER_SIZE_SHIFT = 4;

function byteTexture(): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Uint8Array(HEX_COUNT * 4), COLS, ROWS, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

function texBytes(tex: THREE.DataTexture): Uint8Array {
  return tex.image.data as Uint8Array;
}

/** Decoded terrain texel: each value is an integer-valued float node. */
export interface TerrainTexel {
  terrain: F;
  feature: F;
  hill: F;
  riverSize: F;
  /** Bitmask, bit d = river along edge d. */
  river: F;
}

export class HexData {
  readonly terrain = byteTexture();
  readonly relief: THREE.DataTexture;
  readonly owner = byteTexture();
  readonly state = byteTexture();
  readonly mark = byteTexture();
  readonly ground: THREE.DataTexture;
  readonly snow: THREE.DataTexture;
  readonly actors: THREE.DataTexture;

  /** Hexes changed by the last snapshot upload (for tests and stats). */
  lastChanged = 0;

  private highlighted: number[] = [];
  /** Ocean and coast hexes (they take sea ice from snapshots). */
  private readonly sea = new Uint8Array(HEX_COUNT);
  /** Physical-grid ice per hex: ice shelves and glaciers, which stay whatever the month's sea ice says. */
  private readonly baseIce = new Uint8Array(HEX_COUNT);

  constructor() {
    const half = new Uint16Array(HEX_COUNT * 4);
    this.relief = new THREE.DataTexture(half, COLS, ROWS, THREE.RGBAFormat, THREE.HalfFloatType);
    this.relief.magFilter = THREE.LinearFilter;
    this.relief.minFilter = THREE.LinearFilter;
    this.relief.wrapS = THREE.RepeatWrapping;
    this.relief.wrapT = THREE.ClampToEdgeWrapping;
    this.relief.generateMipmaps = false;
    this.relief.needsUpdate = true;

    this.ground = new THREE.DataTexture(new Uint8Array(HEX_COUNT * GROUND_COUNT), COLS * 2, ROWS, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.ground.magFilter = THREE.NearestFilter;
    this.ground.minFilter = THREE.NearestFilter;
    this.ground.generateMipmaps = false;
    this.ground.needsUpdate = true;

    this.snow = new THREE.DataTexture(new Uint16Array(HEX_COUNT * 4), COLS, ROWS, THREE.RGBAFormat, THREE.HalfFloatType);
    this.snow.magFilter = THREE.NearestFilter;
    this.snow.minFilter = THREE.NearestFilter;
    this.snow.generateMipmaps = false;
    this.snow.needsUpdate = true;

    this.actors = new THREE.DataTexture(new Uint8Array(ACTOR_TEX_W * ACTOR_ROWS * 4), ACTOR_TEX_W, ACTOR_ROWS, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.actors.magFilter = THREE.NearestFilter;
    this.actors.minFilter = THREE.NearestFilter;
    this.actors.generateMipmaps = false;
    this.actors.needsUpdate = true;

    // Lens starts empty everywhere.
    const st = texBytes(this.state);
    for (let i = 0; i < HEX_COUNT; i++) {
      st[i * 4 + 3] = LENS_NONE;
    }
  }

  /** Upload the physical grid (once at load). */
  setPhysical(g: PhysicalGrid): void {
    const t = texBytes(this.terrain);
    const r = this.relief.image.data as Uint16Array;
    const toHalf = THREE.DataUtils.toHalfFloat;
    for (let i = 0; i < HEX_COUNT; i++) {
      t[i * 4] = g.terrain[i];
      t[i * 4 + 1] = g.feature[i];
      t[i * 4 + 2] = g.hill[i] + (g.river_size[i] << RIVER_SIZE_SHIFT);
      t[i * 4 + 3] = g.river[i];
      r[i * 4] = toHalf(g.elev[i]);
      r[i * 4 + 1] = toHalf(g.elev_max[i]);
      r[i * 4 + 2] = toHalf(g.ice[i] / BYTE);
      r[i * 4 + 3] = toHalf(g.land[i] / BYTE);
      this.sea[i] = g.terrain[i] <= TERRAIN.COAST ? 1 : 0;
      this.baseIce[i] = g.ice[i];
    }
    this.terrain.needsUpdate = true;
    this.relief.needsUpdate = true;
  }

  /** Upload the per-hex ground cover and glacier snow computed by heights.ts (once at load). */
  setShapes(shapes: HexShapes): void {
    (this.ground.image.data as Uint8Array).set(shapes.ground);
    this.ground.needsUpdate = true;
    const sn = this.snow.image.data as Uint16Array;
    const toHalf = THREE.DataUtils.toHalfFloat;
    for (let i = 0; i < HEX_COUNT; i++) {
      sn[i * 4] = toHalf(shapes.snowLine[i]);
      sn[i * 4 + 1] = toHalf(shapes.snowPatch[i]);
    }
    this.snow.needsUpdate = true;
  }

  /** Upload owner/controller/control/staleness from a snapshot; only changed textures upload. */
  setSnapshot(s: WorldSnapshot): void {
    const o = texBytes(this.owner);
    const st = texBytes(this.state);
    let ownerChanged = 0;
    let stateChanged = 0;
    for (let i = 0; i < HEX_COUNT; i++) {
      const at = i * 4;
      const own = s.owner[i];
      const ctl = s.controller[i];
      if (o[at] !== (own & BYTE) || o[at + 1] !== own >> 8 || o[at + 2] !== (ctl & BYTE) || o[at + 3] !== ctl >> 8) {
        o[at] = own & BYTE;
        o[at + 1] = own >> 8;
        o[at + 2] = ctl & BYTE;
        o[at + 3] = ctl >> 8;
        ownerChanged++;
      }
      if (st[at] !== s.control[i] || st[at + 1] !== s.controlFrac[i] || st[at + 2] !== s.staleness[i]) {
        st[at] = s.control[i];
        st[at + 1] = s.controlFrac[i];
        st[at + 2] = s.staleness[i];
        stateChanged++;
      }
    }
    if (ownerChanged > 0) {
      this.owner.needsUpdate = true;
    }
    if (stateChanged > 0) {
      this.state.needsUpdate = true;
    }
    this.lastChanged = Math.max(ownerChanged, stateChanged);
    this.setRelations(s.relations);
    this.setSeaIce(s.seaIce);
  }

  /** Sea-ice concentration (0..255 per hex) into the relief texture's ice channel on sea hexes. Shelf ice from the grid always stays. */
  private setSeaIce(ice: Uint8Array | undefined): void {
    const r = this.relief.image.data as Uint16Array;
    const toHalf = THREE.DataUtils.toHalfFloat;
    let changed = 0;
    for (let i = 0; i < HEX_COUNT; i++) {
      if (this.sea[i] === 0) {
        continue;
      }
      const v = toHalf(Math.max(this.baseIce[i], ice ? ice[i] : 0) / BYTE);
      if (r[i * 4 + 2] !== v) {
        r[i * 4 + 2] = v;
        changed++;
      }
    }
    if (changed > 0) {
      this.relief.needsUpdate = true;
    }
  }

  /** Relation of each actor to the viewing leader (row 2 of the actors texture). */
  private setRelations(relations: WorldSnapshot['relations']): void {
    const a = texBytes(this.actors);
    const row = ACTOR_TEX_W * 2 * 4;
    a.fill(0, row, row + ACTOR_TEX_W * 4);
    for (const [index, info] of relations) {
      if (index >= 0 && index < ACTOR_TEX_W) {
        a[row + index * 4] = RELATION_CODE[info.relation] ?? 0;
      }
    }
    this.actors.needsUpdate = true;
  }

  /** Upload a lens (scalar 0..1 or category index); null clears it. */
  setLens(lens: LensResult | null): void {
    const st = texBytes(this.state);
    let changed = 0;
    for (let i = 0; i < HEX_COUNT; i++) {
      const v = lens ? lens.values[i] : NaN;
      let b = LENS_NONE;
      if (!Number.isNaN(v)) {
        b = lens?.kind === 'category' ? Math.min(LENS_MAX, Math.round(v)) : Math.round(Math.min(1, Math.max(0, v)) * LENS_MAX);
      }
      if (st[i * 4 + 3] !== b) {
        st[i * 4 + 3] = b;
        changed++;
      }
    }
    if (changed > 0) {
      this.state.needsUpdate = true;
    }
  }

  /** Mark hexes as highlighted (selection, notification focus); replaces the previous set. */
  setHighlight(hexes: readonly number[]): void {
    const m = texBytes(this.mark);
    for (const h of this.highlighted) {
      m[h * 4] = 0;
    }
    this.highlighted = hexes.filter((h) => h >= 0 && h < HEX_COUNT);
    for (const h of this.highlighted) {
      m[h * 4] = BYTE;
    }
    this.mark.needsUpdate = true;
  }

  /** Actor colours by actor index (index 0 = no owner). Colours are CSS strings. */
  setActorColors(colors: ReadonlyArray<{ primary: string; secondary: string } | null>): void {
    const a = texBytes(this.actors);
    const c = new THREE.Color();
    const n = Math.min(colors.length, ACTOR_TEX_W);
    for (let i = 0; i < n; i++) {
      const pair = colors[i];
      if (!pair) {
        continue;
      }
      c.set(pair.primary);
      a.set([Math.round(c.r * BYTE), Math.round(c.g * BYTE), Math.round(c.b * BYTE), BYTE], i * 4);
      c.set(pair.secondary);
      a.set([Math.round(c.r * BYTE), Math.round(c.g * BYTE), Math.round(c.b * BYTE), BYTE], (ACTOR_TEX_W + i) * 4);
    }
    this.actors.needsUpdate = true;
  }

  // ─── TSL accessors ─────────────────────────────────────────────────────────────────────────────

  /** Terrain texel of a cell, decoded to integer-valued floats. */
  terrainAt(cell: HexCell): TerrainTexel {
    const v = textureLoad(this.terrain, cellCoord(cell)).mul(BYTE).round();
    const b = v.z;
    const riverSize = b.div(1 << RIVER_SIZE_SHIFT).floor();
    return { terrain: v.x, feature: v.y, hill: b.sub(riverSize.mul(1 << RIVER_SIZE_SHIFT)), riverSize, river: v.w };
  }

  /** Relief texel of a cell: (elev m, elev_max m, ice 0..1, land 0..1). */
  reliefAt(cell: HexCell): Node<'vec4'> {
    return textureLoad(this.relief, cellCoord(cell));
  }

  /** Relief interpolated between the three hex centres around a fragment (smooth depth, ice, land). */
  reliefSmooth(frag: HexFrag): Node<'vec4'> {
    const { cells, w } = hexBary(frag);
    return this.reliefAt(cells[0]).mul(w.x).add(this.reliefAt(cells[1]).mul(w.y)).add(this.reliefAt(cells[2]).mul(w.z));
  }

  /** Owner actor index of a cell (0 = none). */
  ownerAt(cell: HexCell): F {
    const v = textureLoad(this.owner, cellCoord(cell)).mul(BYTE).round();
    return v.x.add(v.y.mul(256));
  }

  /** Controller actor index of a cell (0 = same as owner). */
  controllerAt(cell: HexCell): F {
    const v = textureLoad(this.owner, cellCoord(cell)).mul(BYTE).round();
    return v.z.add(v.w.mul(256));
  }

  /** State texel of a cell in 0..255: (CONTROL, controlFrac, staleness, lens). */
  stateAt(cell: HexCell): Node<'vec4'> {
    return textureLoad(this.state, cellCoord(cell)).mul(BYTE).round();
  }

  /** Mark texel of a cell in 0..1: (highlight, spare, spare, spare). */
  markAt(cell: HexCell): Node<'vec4'> {
    return textureLoad(this.mark, cellCoord(cell));
  }

  /** Linear colour of an actor; `row` 0 = primary, 1 = secondary. */
  actorColor(index: F, row: 0 | 1): Node<'vec3'> {
    const x = index.min(float(ACTOR_TEX_W - 1));
    return vec3(textureLoad(this.actors, ivec2(int(x), int(row))).xyz);
  }

  /** RELATION_CODE of an actor toward the viewing leader (0 = unknown). */
  actorRelation(index: F): F {
    const x = index.min(float(ACTOR_TEX_W - 1));
    return textureLoad(this.actors, ivec2(int(x), int(2))).x.mul(BYTE).round();
  }

  /** Ground-cover weights of a cell: (grass, plains, desert, tundra) and (snow, broadleaf, conifer, jungle). */
  groundAt(cell: HexCell): { a: Node<'vec4'>; b: Node<'vec4'> } {
    const x = int(cell.col).mul(2);
    const y = int(cell.row);
    return { a: textureLoad(this.ground, ivec2(x, y)), b: textureLoad(this.ground, ivec2(x.add(1), y)) };
  }

  /** Ground cover interpolated between the three hex centres around a (warped) fragment. */
  groundSmooth(frag: HexFrag): { a: Node<'vec4'>; b: Node<'vec4'> } {
    const { cells, w } = hexBary(frag);
    const g0 = this.groundAt(cells[0]);
    const g1 = this.groundAt(cells[1]);
    const g2 = this.groundAt(cells[2]);
    return {
      a: g0.a.mul(w.x).add(g1.a.mul(w.y)).add(g2.a.mul(w.z)),
      b: g0.b.mul(w.x).add(g1.b.mul(w.y)).add(g2.b.mul(w.z)),
    };
  }

  /** Glacier snow of a cell: (snow line y, patch share, 0, 0). */
  snowAt(cell: HexCell): Node<'vec4'> {
    return textureLoad(this.snow, cellCoord(cell));
  }

  dispose(): void {
    for (const t of [this.terrain, this.relief, this.owner, this.state, this.mark, this.ground, this.snow, this.actors]) {
      t.dispose();
    }
  }
}
